import { type Editor } from '@tiptap/core';
import { Fragment, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { CellSelection, cellAround, isInTable, selectedRect } from '@tiptap/pm/tables';
// The section-boundary guard in `moveNode` names the section node through its
// schema single source of truth, so a rename can't silently disarm it.
import { sectionNodeSchemaSpec } from '../sections/section-node-schema';

// CellAlign is kernel vocabulary (the table markdown spec's align attribute);
// it lives with the packaged table-markdown and is re-exported here for the
// menu-side consumers (task 5.1).
import { isCellAlign, type CellAlign } from '../table-markdown';

export { isCellAlign, type CellAlign };

/**
 * Finds the table node position and node from the current selection.
 */
export function findTable(editor: Editor) {
  const { state } = editor;
  const { $from } = state.selection;

  for (let d = $from.depth; d >= 1; d--) {
    if ($from.node(d).type.name === 'table') {
      return {
        pos: $from.before(d),
        node: $from.node(d),
        depth: d,
      };
    }
  }
  return null;
}

/**
 * True when the hopped child sequence is valid content for `parent`.
 * A `listItem` cannot start with a table (`paragraph block*`); without this
 * check, ProseMirror's Fitter inserts a filler paragraph that serializes.
 */
function tableHopIsValid(
  parent: ProseMirrorNode,
  tableIndex: number,
  visibleIndex: number
): boolean {
  const children: ProseMirrorNode[] = [];
  parent.forEach((child) => children.push(child));
  // marked can emit a headless `listItem` when a bullet opens on a pipe row;
  // `contentMatchAt` then throws on the intermediate delete.
  if (!parent.type.validContent(parent.content)) return false;
  const [table] = children.splice(tableIndex, 1);
  if (!table) return false;
  // After removing the table, `visibleIndex` is the insertion slot in both directions.
  children.splice(visibleIndex, 0, table);
  return parent.type.validContent(Fragment.from(children));
}

/**
 * The sibling range `moveNode` swaps over the table when moving in
 * `direction`: the nearest visible sibling plus every blank-line paragraph
 * between it and the table. Returns `null` when there is no such range —
 * first/last child of any parent, only blank padding in that direction, or
 * (`up` only) the section's title heading sits immediately above — and when
 * the resulting arrangement would violate the parent's content model
 * ({@link tableHopIsValid}).
 *
 * Shared by {@link moveNode} and {@link isMoveNodeDisabled} so the command
 * and its disabled-state predicate cannot drift apart.
 */
function findMoveSwapGroup(
  doc: Editor['state']['doc'],
  table: NonNullable<ReturnType<typeof findTable>>,
  direction: 'up' | 'down'
): { from: number; to: number } | null {
  const tableEnd = table.pos + table.node.nodeSize;

  // A markdown blank line as vMarkdown/v1 store it: a paragraph with no
  // content at all. Anything with content (even a hard break) renders.
  const isBlankParagraph = (node: typeof table.node) =>
    node.type.name === 'paragraph' && node.content.size === 0;

  if (direction === 'up') {
    // Can't move up if table is the first node in the document
    if (table.pos <= 0) return null;

    const $before = doc.resolve(table.pos);
    const indexInParent = $before.index($before.depth);
    if (indexInParent === 0) return null;

    const parent = $before.node($before.depth);

    // Walk past blank-line paragraphs to the nearest visible sibling. Only
    // blank padding above means there is nothing visible to cross: refuse,
    // or the press would reorder the saved markdown with no visual change.
    let visibleIndex = indexInParent - 1;
    while (visibleIndex >= 0 && isBlankParagraph(parent.child(visibleIndex))) {
      visibleIndex--;
    }
    if (visibleIndex < 0) return null;

    // A section's first child is its title heading — the `#` line that opens
    // the section on reparse. The schema (`block+`) would allow the swap, and
    // the section node view would keep rendering the table inside the section
    // card, but the serialized markdown puts the table before the `#` boundary,
    // so a reload silently re-homes it into the previous section. What the
    // user sees and what saves diverge — structure, not content: refuse, the
    // same way the down branch refuses at the section's last child.
    if (
      parent.type.name === sectionNodeSchemaSpec.name &&
      visibleIndex === 0 &&
      parent.child(visibleIndex).type.name === 'heading'
    ) {
      return null;
    }

    if (!tableHopIsValid(parent, indexInParent, visibleIndex)) return null;

    let from = table.pos;
    for (let i = visibleIndex; i < indexInParent; i++) {
      from -= parent.child(i).nodeSize;
    }
    return { from, to: table.pos };
  }

  // Can't move down if table is the last node
  if (tableEnd >= doc.content.size) return null;

  const $before = doc.resolve(table.pos);
  const indexInParent = $before.index($before.depth);
  const parent = $before.node($before.depth);
  if (indexInParent >= parent.childCount - 1) return null;

  // Mirror of the up branch: the group is every blank-line paragraph below
  // the table plus the first visible sibling. Only blank padding below —
  // including a section's trailing blank lines — refuses the same way.
  let visibleIndex = indexInParent + 1;
  while (
    visibleIndex < parent.childCount &&
    isBlankParagraph(parent.child(visibleIndex))
  ) {
    visibleIndex++;
  }
  if (visibleIndex >= parent.childCount) return null;

  if (!tableHopIsValid(parent, indexInParent, visibleIndex)) return null;

  let to = tableEnd;
  for (let i = indexInParent + 1; i <= visibleIndex; i++) {
    to += parent.child(i).nodeSize;
  }
  return { from: tableEnd, to };
}

/**
 * Moves a top-level node (like a table) up or down by swapping it with its
 * visible neighbour.
 *
 * Both branches move the **siblings** over the table, never the table itself:
 * the selection lives inside the table, and a step that deletes the range
 * holding it collapses it to the deletion boundary, which lands outside the
 * table once the table is re-inserted elsewhere. That closes the table menu
 * mid-click — it is a pure function of {@link shouldShowTableMenu} — and
 * downgrades a `CellSelection` to a caret (SCR-6164). Deleting the siblings
 * instead leaves the selection outside every deleted range, so ProseMirror's
 * own step mapping carries it: a caret keeps its in-cell offset and a
 * `CellSelection` stays one, with no explicit `setSelection` to keep in sync.
 *
 * The swapped group is the nearest sibling the user can *see* plus every
 * blank-line paragraph between it and the table. The vMarkdown/v1 editors
 * materialize each markdown blank line as an empty `paragraph` node, so a
 * single-sibling swap there crosses an invisible node — one transaction,
 * `docChanged`, and a document that renders byte-identically: the button reads
 * as dead. Hopping the whole group keeps one press = one visible step on every
 * surface, and degenerates to the plain single-sibling swap when there are no
 * blank paragraphs (markdown-native's canonical parse never produces them).
 *
 * Refusals are silent no-ops; the menu surfaces them via
 * {@link isMoveNodeDisabled}.
 */
export function moveNode(editor: Editor, direction: 'up' | 'down') {
  const table = findTable(editor);
  if (!table) return;

  const { state } = editor;
  const { doc } = state;
  const groupRange = findMoveSwapGroup(doc, table, direction);
  if (!groupRange) return;

  // Complete top-level siblings, so the slice's content is a flat fragment.
  const group = doc.slice(groupRange.from, groupRange.to).content;
  const { tr } = state;
  tr.delete(groupRange.from, groupRange.to);
  if (direction === 'up') {
    // Swap: lift the group over the table (see the invariant above). After
    // the delete, the group's old slot holds the table, so the table's new
    // end — where the group goes — is `groupRange.from + table.node.nodeSize`.
    tr.insert(groupRange.from + table.node.nodeSize, group);
  } else {
    // Swap: remove the group, insert before table
    tr.insert(table.pos, group);
  }
  editor.view.dispatch(tr);
}

/**
 * True when {@link moveNode} would be a no-op for the current selection in
 * `direction`. Returns `false` when the selection is not in a table, matching
 * {@link isDeleteRowDisabled} / {@link isDeleteColumnDisabled}: "nothing to
 * act on" is not a boundary disable.
 */
export function isMoveNodeDisabled(editor: Editor, direction: 'up' | 'down'): boolean {
  const table = findTable(editor);
  if (!table) return false;
  return findMoveSwapGroup(editor.state.doc, table, direction) === null;
}

/**
 * True only for schemas whose table cells carry the `align` attribute — the
 * markdown-native kit (`withCellAlign`). vMarkdown/v1 use the stock table
 * schema, which can't persist GFM-only table features, so this doubles as the
 * gate for every markdown-native-only table control.
 */
export function tableCellSupportsAlign(editor: Editor): boolean {
  return 'align' in (editor.schema.nodes.tableCell?.spec.attrs ?? {});
}

/** True when every cell in the table's first row is a header cell. */
export function isHeaderRowActive(editor: Editor): boolean {
  const firstRow = findTable(editor)?.node.firstChild;
  if (!firstRow) return false;

  let allHeader = true;
  firstRow.forEach((cell) => {
    if (cell.type.spec.tableRole !== 'header_cell') allHeader = false;
  });
  return allHeader;
}

/**
 * True when `deleteColumn` would be a no-op for the current selection — i.e.
 * the selection spans every column, so removing it would empty the table.
 *
 * `prosemirror-tables` refuses this (a table needs one column) but hides the
 * guard inside a dispatch-only branch, so `editor.can().deleteColumn()` can't
 * detect it — the dry run stubs `dispatch`, skipping the guard entirely. We
 * replicate the library's own check off `selectedRect` so it stays in lockstep:
 * a plain cursor in a one-column table and a cell-selection dragged across a
 * whole row both report `true`.
 */
export function isDeleteColumnDisabled(editor: Editor): boolean {
  if (!isInTable(editor.state)) return false;
  const rect = selectedRect(editor.state);
  return rect.left === 0 && rect.right === rect.map.width;
}

/** True when `deleteRow` would empty the table (selection spans every row). Same `prosemirror-tables` quirk as {@link isDeleteColumnDisabled}. */
export function isDeleteRowDisabled(editor: Editor): boolean {
  if (!isInTable(editor.state)) return false;
  const rect = selectedRect(editor.state);
  return rect.top === 0 && rect.bottom === rect.map.height;
}

/**
 * The table menu's visibility rule: **selection means formatting, caret means
 * structure.** Inside a table a caret keeps the table menu, while selecting
 * text within a cell hands the floating slot to the text menu so the two never
 * stack on screen.
 *
 * A `CellSelection` is the exception. Dragging across cells produces one, and
 * because it is neither empty nor a `TextSelection` the text-selection helpers
 * report it as "text is selected" — yet the row, column and alignment actions
 * are exactly what it is for, so it keeps the table menu.
 *
 * The text menu reads this same predicate and stands down when it is true,
 * which is what makes the two menus mutually exclusive.
 */
export function shouldShowTableMenu(editor: Editor): boolean {
  // Read-only surfaces mount this menu too, and every action it offers mutates
  // the table — the text menu bails on the same condition.
  if (!editor.isEditable) return false;
  if (!editor.isActive('table')) return false;
  if (editor.state.selection instanceof CellSelection) return true;

  const { doc, selection } = editor.state;
  return doc.textBetween(selection.from, selection.to).length === 0;
}

/**
 * Selects every cell of the table the selection sits in, as one `CellSelection`.
 * A table can never be node-selected (`allowTableNodeSelection` is off), so
 * dragging corner to corner was the only gesture that produced this.
 *
 * Corners come off `TableMap.map`, whose slots hold the start of the cell
 * covering them. Slots no cell covers keep the map's `0` seed, and `0` is never
 * a cell start — so any `0` is a hole, and a holed table is refused rather than
 * partly selected: resolving a hole yields a non-cell, which either throws or
 * puts a row node in a cell's place and mangles the table on the next Backspace.
 * `positionAt` is not interchangeable here; on a bottom-right rowspan
 * continuation it returns the row's end, which is also not a cell start.
 *
 * Read-only editors are included deliberately — selecting a table to copy it is
 * the point — hence no `isEditable` gate like `shouldShowTableMenu`'s.
 */
export function selectTable(editor: Editor): void {
  const { state } = editor;
  if (!isInTable(state)) return;

  const { map, tableStart } = selectedRect(state);
  const { tr } = state;
  const firstCell = map.map.at(0);
  const lastCell = map.map.at(-1);
  if (!firstCell || !lastCell || map.map.includes(0)) return;

  const $firstCell = tr.doc.resolve(tableStart + firstCell);
  const $lastCell = tr.doc.resolve(tableStart + lastCell);

  editor.view.dispatch(tr.setSelection(new CellSelection($firstCell, $lastCell)));
}

/** Reads the `align` attribute of the cell under the cursor. */
export function getCurrentColumnAlign(editor: Editor): CellAlign | null {
  const align: unknown = cellAround(editor.state.selection.$from)?.nodeAfter?.attrs.align;
  return isCellAlign(align) ? align : null;
}

/**
 * Sets `align` on every cell in the cursor's column (GFM alignment is
 * column-scoped). `colSelection` only enumerates the column — it is never
 * dispatched, so the user's cursor stays put. One transaction, one undo step.
 */
export function setColumnAlign(editor: Editor, align: CellAlign | null): void {
  const { state } = editor;
  const $cell = cellAround(state.selection.$from);
  if (!$cell) return;

  const { tr } = state;
  CellSelection.colSelection($cell).forEachCell((node, pos) => {
    if (node.attrs.align !== align) {
      tr.setNodeMarkup(pos, null, { ...node.attrs, align });
    }
  });
  if (!tr.docChanged) return;

  editor.view.dispatch(tr);
}
